import React, { useState } from "react";
import { Mail, Phone, ArrowUpRight } from "lucide-react";
import TechStackIcons from "./components/TechStackIcons";
import Header from "./components/Header";
import HomeSection from "./components/HomeSection";
import AboutSection from "./components/AboutSection";
import { FaGithub } from "react-icons/fa";
import { FaLinkedin } from "react-icons/fa6";

// Theme styles live in src/theme.css (imported in index.js)

// Main App Component
export default function App() {
  const [currentPage, setCurrentPage] = useState("home");

  const renderPage = () => {
    switch (currentPage) {
      case "home":
        return <HomeSection setCurrentPage={setCurrentPage} />;
      case "about":
        return <AboutSection />;
      case "skills":
        return <SkillsSection />;
      case "projects":
        return <ProjectsSection />;
      case "contact":
        return <ContactSection />;
      default:
        return <HomeSection setCurrentPage={setCurrentPage} />;
    }
  };

  return (
    <div className="t-page">
      <Header setCurrentPage={setCurrentPage} currentPage={currentPage} />

      <main
        className={`t-panel t-shell ${
          currentPage === "about" ? "t-panel--deep" : ""
        }`}
      >
        {/* Faint moving tech icons as background texture */}
        <div className="t-panel__bg">
          <TechStackIcons />
        </div>

        {/* key re-triggers the page transition animation */}
        <div key={currentPage} className="t-panel__content page-transition-enter">
          {renderPage()}
        </div>
      </main>

      <Footer />
    </div>
  );
}

const pad = (n) => String(n).padStart(2, "0");

// Skills Section Component
function SkillsSection() {
  const skills = {
    Frontend: [
      "React",
      "JavaScript (ES6+)",
      "TypeScript",
      "HTML5",
      "CSS3",
      "Tailwind CSS",
      "Redux",
      "Vue.js",
    ],
    Backend: [
      "Node.js",
      "Express.js",
      "Python",
      "Django",
      "Flask",
      "RESTful APIs",
      "GraphQL",
    ],
    Databases: ["MongoDB", "PostgreSQL", "MySQL", "Firebase Firestore"],
    "Tools & Platforms": [
      "Git",
      "GitHub",
      "Docker",
      "AWS (EC2, S3)",
      "Netlify",
      "Vercel",
      "Jira",
      "VS Code",
    ],
    Methodologies: ["Agile", "Scrum", "Test-Driven Development (TDD)", "CI/CD"],
  };

  return (
    <section id="skills">
      <h2 className="t-display t-title">
        My <span className="t-text-yellow">Skills</span>
      </h2>
      <div className="t-grid">
        {Object.entries(skills).map(([category, skillList], i) => (
          <div key={category} className="t-card">
            <div className="t-card__body">
              <div className="t-card__head">
                <h3 className="t-display t-card__title">{category}</h3>
                <span className="t-card__num">{pad(i + 1)}</span>
              </div>
              <div className="t-chips">
                {skillList.map((skill) => (
                  <span key={skill} className="t-chip">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// Projects Section Component
function ProjectsSection() {
  const projects = [
    {
      title: "Raiwear E-commerce Platform",
      description:
        "A full-stack e-commerce application with user authentication, product listings, shopping cart functionality, and payment integration.",
      tech: ["React", "Tailwind CSS"],
      github: "https://github.com/Taelo1829/properRaiwear",
      live: "https://bright-froyo-2aeb6d.netlify.app/",
    },
    {
      title: "Store Location Updater",
      description:
        "A React-based web tool that allows users to upload Excel files containing store coordinates, adjust locations via Google Maps, and export updated data.",
      tech: ["React", "Google Maps API", "XLSX.js"],
      github: "https://github.com/Taelo1829/storeMapUpdate",
      live: "https://updatestorecoordinates.netlify.app/",
    },
    {
      title: "Task Management App",
      description:
        "A responsive task management tool that allows users to create, organize, and track tasks with drag-and-drop functionality.",
      tech: ["React", "Firebase", "Tailwind CSS"],
      github: "https://github.com/Taelo1829/task-manager",
      live: "https://taelo-task-manager.netlify.app/",
    },
    {
      title: "Tyro Study",
      description:
        "An installable study app (PWA) for working through course question banks, with secure sign-in, file uploads and practice built around real assignment questions.",
      tech: ["Next.js", "PostgreSQL", "Cloudflare R2", "PWA"],
      github: "https://github.com/Taelo1829/tyro-study",
      live: "https://www.tyrostudy.co.za/",
    },
  ];

  return (
    <section id="projects">
      <h2 className="t-display t-title">
        My <span className="t-text-yellow">Projects</span>
      </h2>
      <div className="t-grid">
        {projects.map((project, index) => (
          <article key={project.title} className="t-card">
            <div className="t-card__band">
              <span className="t-card__num">{pad(index + 1)}</span>
              <span className="t-eyebrow">Project</span>
            </div>
            <div className="t-card__body">
              <h3 className="t-display t-card__title">{project.title}</h3>
              <p className="t-card__desc">{project.description}</p>
              <div className="t-chips" style={{ marginBottom: 20 }}>
                {project.tech.map((tech) => (
                  <span key={tech} className="t-chip t-chip--outline">
                    {tech}
                  </span>
                ))}
              </div>
              <div className="t-card__footer">
                <a
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="t-link"
                >
                  <FaGithub size={16} /> GitHub
                </a>
                {project.live && project.live !== "#" && (
                  <a
                    href={project.live}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="t-btn t-btn--sm"
                    style={{ marginLeft: "auto" }}
                  >
                    Live demo <ArrowUpRight size={14} />
                  </a>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

// Contact Section Component
function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    setFormData({ name: "", email: "", message: "" });
    setSent(true);
  };

  return (
    <section id="contact" className="t-contact">
      <h2 className="t-display t-contact__big">Have a project?</h2>
      <h2 className="t-display t-contact__big t-contact__right">
        Let&apos;s talk
      </h2>
      <p className="t-contact__lead">
        Have a question or want to collaborate? Fill out the form below or
        connect with me via social media.
      </p>

      <form onSubmit={handleSubmit} className="t-form">
        <div className="t-form__row">
          <div>
            <label htmlFor="name" className="t-label">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="t-input"
            />
          </div>
          <div>
            <label htmlFor="email" className="t-label">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="t-input"
            />
          </div>
        </div>
        <div>
          <label htmlFor="message" className="t-label">
            Message
          </label>
          <textarea
            id="message"
            name="message"
            rows="5"
            value={formData.message}
            onChange={handleChange}
            required
            className="t-input"
          ></textarea>
        </div>
        <div>
          <button type="submit" className="t-btn">
            Send message <ArrowUpRight size={16} />
          </button>
        </div>
      </form>

      {sent && (
        <div className="t-dialog" role="dialog" aria-modal="true">
          <div className="t-dialog__box">
            <h3 className="t-display" style={{ fontSize: "2rem" }}>
              Message sent
            </h3>
            <p>Thank you for your message! I will get back to you shortly.</p>
            <button className="t-btn" onClick={() => setSent(false)}>
              OK
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

// Footer Component
function Footer() {
  const socials = [
    {
      href: "https://github.com/Taelo1829",
      icon: <FaGithub size={18} />,
      label: "GitHub",
      external: true,
    },
    {
      href: "https://www.linkedin.com/in/taelo-seholoba-566940432/",
      icon: <FaLinkedin size={18} />,
      label: "LinkedIn",
      external: true,
    },
    { href: "tel:0742019041", icon: <Phone size={18} />, label: "Phone" },
    {
      href: "mailto:tseholoba2@gmail.com",
      icon: <Mail size={18} />,
      label: "Email",
    },
  ];

  return (
    <footer className="t-footer t-shell">
      <div className="t-footer__top">
        <div>
          <p className="t-eyebrow">Email me for any queries</p>
          <a href="mailto:tseholoba2@gmail.com" className="t-footer__email">
            tseholoba2@gmail.com
          </a>
        </div>
        <div>
          <p className="t-eyebrow">Follow me</p>
          <div className="t-socials">
            {socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                aria-label={s.label}
                className="t-social"
                {...(s.external
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
              >
                {s.icon}
              </a>
            ))}
          </div>
        </div>
      </div>
      <div className="t-footer__bottom">
        &copy; {new Date().getFullYear()} Taelo Seholoba. All rights reserved.
      </div>
    </footer>
  );
}
